/**
 * The desktop crew lockup and its dropdown.
 * `design/Games Tonight.dc.html` lines 51-71 — the SHELL mirror, not
 * `GT Crew.dc.html`, which owns the crew screens only.
 *
 * NOT A MODAL, deliberately. The mirror draws an absolutely-positioned 320px
 * panel anchored inside the lockup, so it sits under the header's own stacking
 * context and moves with it. A `Modal` would centre it and dim the page, which
 * is the wrong idiom for a menu.
 *
 * That means dismissal is this component's job rather than the primitive's, and
 * the mirror does it three ways: a document-level CAPTURE click that spares
 * anything inside `[data-sw]`, Escape, and every navigating action closing it
 * on the way out. All three are here — the first two below, the third in the
 * actions themselves (`switchCrew` and `openWizard` both close it).
 *
 * A SINGLE-CREW ACCOUNT KEEPS THE CARET AND THE MENU. That reverses the
 * 2026-08-13 spec on purpose: `+ START ANOTHER CREW` lives in this menu and is
 * the only door to a second crew, so a plain label would make starting one
 * unreachable. Only an account in NO crews loses it.
 */

import { useEffect, useRef } from 'react';

import { useQd } from '../../store/index.ts';
import { switcherVM } from '../../store/selectors.ts';
import { useEscape } from './Modal.tsx';

const MONO = 'var(--gt-font-mono)';

export default function CrewSwitcher(): JSX.Element {
  const s = useQd();
  const v = switcherVM(s);
  const wrap = useRef<HTMLDivElement | null>(null);

  useEscape(() => s.closeSw(), v.open);

  useEffect(() => {
    if (!v.open) return;
    // CAPTURE phase, matching the mirror: a click that opens something else
    // must still close this, and capture runs before that handler can stop
    // propagation. `[data-sw]` is the whitelist — clicking the lockup itself,
    // or anything inside the menu, is not "outside".
    const onDoc = (e: MouseEvent): void => {
      const el = e.target as Element | null;
      if (el?.closest?.('[data-sw]')) return;
      s.closeSw();
    };
    document.addEventListener('click', onDoc, true);
    return () => document.removeEventListener('click', onDoc, true);
  }, [v.open, s]);

  return (
    <div data-sw="" ref={wrap} style={{ position: 'relative', flex: 'none' }}>
      <div
        onClick={() => (v.open ? s.closeSw() : v.canOpen ? s.openSw() : undefined)}
        title={v.tip}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: '1px solid var(--gt-border-2)',
          background: 'var(--gt-panel)',
          padding: '6px 10px',
          cursor: v.canOpen ? 'pointer' : 'default',
        }}
      >
        <span
          style={{
            fontFamily: MONO,
            fontSize: '11px',
            letterSpacing: '.04em',
            color: 'var(--gt-text)',
            whiteSpace: 'nowrap',
            // The 118px cap is the mirror's, and it is also what the measured
            // compact breakpoint is computed from.
            maxWidth: '118px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {v.lockupTxt}
        </span>
        {v.canOpen ? (
          <span style={{ fontFamily: MONO, fontSize: '9px', color: 'var(--gt-muted)', lineHeight: 1 }}>
            ▾
          </span>
        ) : null}
      </div>

      {v.open ? (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 6,
            width: 320,
            background: 'var(--gt-card)',
            border: '1px solid var(--gt-border)',
            boxShadow: 'var(--gt-shadow-toast)',
            zIndex: 60,
          }}
        >
          <div
            style={{
              padding: '8px 12px',
              borderBottom: '1px solid var(--gt-line)',
              fontFamily: MONO,
              fontSize: '8.5px',
              letterSpacing: '.08em',
              color: 'var(--gt-muted-2)',
            }}
          >
            YOUR PARTIES
          </div>

          {v.rows.map((c) => (
            <div
              key={c.key}
              onClick={() => {
                // `switchCrew` drops a click that arrives mid-flight anyway;
                // leaving the menu open is what makes that visible instead of
                // silent. The row cannot be the place a crew switch is lost.
                if (v.busy) return;
                s.closeSw();
                if (!c.on) void s.switchCrew(c.key);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '10px 12px',
                borderBottom: '1px solid var(--gt-line-0)',
                cursor: v.busy ? 'progress' : 'pointer',
                background: c.bg,
              }}
            >
              <span style={{ fontFamily: MONO, fontSize: '11px', color: c.clr, flex: 1, minWidth: 0 }}>
                {c.nameUp}
              </span>
              <span style={{ fontFamily: MONO, fontSize: '8.5px', color: 'var(--gt-muted-2)' }}>
                {c.membersTxt}
              </span>
              <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: c.roleClr }}>
                {c.roleTxt}
              </span>
            </div>
          ))}

          <div
            onClick={() => {
              if (v.busy) return;
              s.closeSw();
              s.openWizard();
            }}
            style={{
              padding: '10px 12px',
              fontFamily: MONO,
              fontSize: '9.5px',
              letterSpacing: '.04em',
              color: v.busy ? 'var(--gt-muted)' : 'var(--acc)',
              cursor: v.busy ? 'progress' : 'pointer',
            }}
          >
            + START ANOTHER PARTY
          </div>
        </div>
      ) : null}
    </div>
  );
}
